import React from 'react';
import { motion } from 'framer-motion';
import { Truck, ArrowRight, PlayCircle, FileEdit, Brain, ShieldCheck, Clock, Timer, Map } from 'lucide-react';
import './LandingPage.css';

const LandingPage = ({ onStart, onNavigate }) => {
    return (
        <div className="landing-page-v2">
            {/* Navigation */}
            <nav className="landing-nav">
                <div className="nav-container">
                    <div className="nav-brand">
                        <div className="brand-icon">
                            <Truck size={24} />
                        </div>
                        <span className="brand-text">SpotLogs</span>
                    </div>
                    <div className="nav-links">
                        <a href="#features">Features</a>
                        <a href="#how-it-works">Compliance</a>
                        <a href="#pricing">Pricing</a>
                        <button className="sign-in-btn" onClick={() => onStart('login')}>Sign In</button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="hero-section">
                <div className="hero-bg">
                    <img 
                        src="https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=1920&q=80" 
                        alt="Semi truck at sunset"
                        className="hero-img"
                    />
                    <div className="hero-gradient"></div>
                </div>

                <div className="hero-content">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        {/* FMCSA Badge */}
                        <div className="fmcsa-badge">
                            <span className="pulse-dot"></span>
                            <span>FMCSA COMPLIANT</span>
                        </div>

                        {/* Main Heading */}
                        <h1 className="hero-title">
                            The Autopilot <br />
                            <span className="gradient-text">for Your Logbook</span>
                        </h1>

                        <p className="hero-subtitle">
                            Focus on the road, not the math. SpotLogs automates your compliance, 
                            tracks drive time, and generates perfect logs instantly.
                        </p>

                        {/* CTA Buttons */}
                        <div className="hero-ctas">
                            <button className="cta-primary" onClick={() => onStart('signup')}>
                                Start Your Free Trip
                                <ArrowRight size={20} />
                            </button>
                            <button className="cta-secondary">
                                <PlayCircle size={20} />
                                Watch Demo
                            </button>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="how-it-works" id="how-it-works">
                <div className="section-header">
                    <h2>How It Works</h2>
                    <div className="section-divider"></div>
                </div>

                <div className="steps-grid">
                    <motion.div 
                        className="step-card"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 }}
                    >
                        <div className="step-icon">
                            <FileEdit size={24} />
                        </div>
                        <h3>1. Input Trip</h3>
                        <p>Enter your starting point, destination, and estimated stops. Our engine understands ELD requirements out of the box.</p>
                    </motion.div>

                    <motion.div 
                        className="step-card"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 }}
                    >
                        <div className="step-icon">
                            <Brain size={24} />
                        </div>
                        <h3>2. Taking Records</h3>
                        <p>Our system trained on the ELD rules and regulations, will show how the trip will look like in terms of driving hours and breaks.</p>
                    </motion.div>

                    <motion.div 
                        className="step-card"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 }}
                    >
                        <div className="step-icon">
                            <ShieldCheck size={24} />
                        </div>
                        <h3>3. Get Legal Logs</h3>
                        <p>Download your ready-to-use, compliant logbooks. No more manual math or logbook violations during inspections.</p>
                    </motion.div>
                </div>
            </section>

            {/* Features Section */}
            <section className="features-section" id="features">
                <div className="features-container">
                    <div className="features-text">
                        <h2>Trucking automation <br />that works for you.</h2>
                        
                        <div className="feature-list">
                            <div className="feature-item">
                                <div className="feature-icon">
                                    <Clock size={20} />
                                </div>
                                <div>
                                    <h4>11h Drive Tracking</h4>
                                    <p>Real-time monitoring of your driving limits to ensure you never go over.</p>
                                </div>
                            </div>

                            <div className="feature-item">
                                <div className="feature-icon">
                                    <Timer size={20} />
                                </div>
                                <div>
                                    <h4>14h Window Compliance</h4>
                                    <p>Automatic alerts before your daily duty period expires.</p>
                                </div>
                            </div>

                            <div className="feature-item">
                                <div className="feature-icon">
                                    <Map size={20} />
                                </div>
                                <div>
                                    <h4>Automated Route Planning</h4>
                                    <p>Plan smarter routes that account for mandated rest breaks.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="features-preview">
                        <div className="preview-card">
                            <img 
                                src="https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?w=600&q=80" 
                                alt="Dashboard Preview"
                                className="preview-img"
                            />
                        </div>
                        <div className="status-card">
                            <div className="status-header">
                                <div className="status-icon">
                                    <ShieldCheck size={16} />
                                </div>
                                <div>
                                    <span className="status-label">Log Status</span>
                                    <span className="status-value">All Compliant</span>
                                </div>
                            </div>
                            <div className="status-bar">
                                <div className="status-fill"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="landing-footer">
                <div className="footer-container">
                    <div className="footer-brand">
                        <div className="brand-icon-small">
                            <Truck size={18} />
                        </div>
                        <span>SpotLogs</span>
                    </div>
                    <p className="footer-copy">© 2026 SpotLogs. All rights reserved.</p>
                    <div className="footer-links">
                        <button className="footer-link-btn" onClick={() => onNavigate('privacy')}>Privacy</button>
                        <button className="footer-link-btn" onClick={() => onNavigate('help')}>Support</button>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;
